/**
 * @description Bundled public-holiday notes for Overview Needs Attention
 *   (LYN-455). date-holidays only — no live calendar API.
 *
 * @dependencies date-holidays, ./scope1Dashboard
 *
 * @notes Off while sales_organizations.country is empty. US is the country
 *   in use and is covered. A holiday dip is a note, not an alarm.
 */

import Holidays from 'date-holidays';
import {
  addZonedDays,
  chartTimeZone,
  weekdayName,
  zonedDateParts,
  zonedDayStart
} from './scope1Dashboard';

const calendars = new Map();

/**
 * @param {unknown} value
 * @returns {string|null}
 */
export function normalizeCountry(value) {
  if (typeof value !== 'string') return null;
  const code = value.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : null;
}

/**
 * @param {string} country
 * @returns {Holidays|null}
 */
function calendarFor(country) {
  const code = normalizeCountry(country);
  if (!code) return null;
  if (!calendars.has(code)) {
    try {
      calendars.set(code, new Holidays(code));
    } catch {
      calendars.set(code, null);
    }
  }
  return calendars.get(code) || null;
}

/**
 * Confirm the bundled calendar can answer a known public holiday.
 *
 * @param {string} country
 * @returns {boolean}
 */
export function holidayCalendarCovers(country) {
  return calendarFor(country) != null;
}

/**
 * Public holiday name on that civil day, or null.
 *
 * @param {object} input
 * @returns {string|null}
 */
export function holidayNameOn({ date, country, timeZone } = {}) {
  const calendar = calendarFor(country);
  if (!calendar || date == null) return null;
  const parts = zonedDateParts(date, timeZone);
  if (![parts.year, parts.month, parts.day].every(Number.isFinite)) return null;
  const ymd = `${String(parts.year).padStart(4, '0')}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
  // date-holidays isHoliday(YYYY-MM-DD) is false; match the civil date
  // string from getHolidays (country-local midnight, not Date tz).
  const found = (calendar.getHolidays(parts.year) || []).find((row) => (
    String(row?.date || '').startsWith(ymd) && row?.type === 'public'
  ));
  return found?.name || null;
}

/**
 * First public holiday in [from, to), display zone.
 *
 * @param {object} input
 * @returns {{ name: string, date: Date }|null}
 */
export function holidayInWindow({ from, to, country, timeZone } = {}) {
  if (!normalizeCountry(country) || !from || !to) return null;
  const tz = chartTimeZone(timeZone);
  let cursor = zonedDayStart(from, tz);
  const end = new Date(to).getTime();
  let steps = 0;
  while (cursor.getTime() < end && steps < 400) {
    const name = holidayNameOn({ date: cursor, country, timeZone: tz });
    if (name) return { name, date: cursor };
    cursor = addZonedDays(cursor, 1, tz);
    steps += 1;
  }
  return null;
}

/**
 * @param {object} input
 * @returns {string|null}
 */
export function holidayVolumeNote({
  current,
  usual,
  weekday,
  holidayName
} = {}) {
  if (holidayName == null || usual == null || Number(usual) <= 0 || current == null) {
    return null;
  }
  const drop = (Number(usual) - Number(current)) / Number(usual);
  if (drop <= 0) return null;
  const day = weekdayName(weekday);
  return `Tows down ${Math.round(drop * 100)} percent against a usual ${day}, likely ${holidayName}`;
}
